/**
 * Drag a box over the app, get a PNG of that box. Ported from ChemZone ERP.
 *
 * The overlay dims the page and shows a crosshair. Dragging draws a clear
 * window through the dim; letting go captures exactly that area. Escape, a
 * right click or a box too small to mean anything cancels.
 *
 * The capture is the page itself redrawn by `modern-screenshot` (DOM to SVG
 * to canvas), so the browser never asks for screen share permission and the
 * picture is only ever of DrTutor. Anything marked `data-screenshot-ignore`
 * (the help button and its panel) is left out of the picture.
 */

import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

const MIN_SIZE = 12;

function normalise(a: { x: number; y: number }, b: { x: number; y: number }): Rect {
  return { x: Math.min(a.x, b.x), y: Math.min(a.y, b.y), w: Math.abs(a.x - b.x), h: Math.abs(a.y - b.y) };
}

/** The page background, so transparent areas do not come out black. */
function pageBackground(): string {
  const body = getComputedStyle(document.body).backgroundColor;
  if (body && body !== "rgba(0, 0, 0, 0)" && body !== "transparent") return body;
  return getComputedStyle(document.documentElement).backgroundColor || "#ffffff";
}

/**
 * Draw what is on screen and cut out `rect`. The body is drawn at the size
 * of the viewport and shifted by the scroll offset, so content lands where
 * the person saw it. Sticky elements are pinned where they were showing.
 */
async function captureArea(rect: Rect): Promise<Blob> {
  const { domToCanvas } = await import("modern-screenshot");
  const scale = Math.min(window.devicePixelRatio || 1, 2);
  const width = window.innerWidth;
  const height = window.innerHeight;
  const { scrollX, scrollY } = window;

  const stuck: HTMLElement[] = [];
  document.body.querySelectorAll<HTMLElement>("*").forEach((el) => {
    if (getComputedStyle(el).position !== "sticky") return;
    const shown = el.getBoundingClientRect();
    const inline = el.style.position;
    el.style.position = "static";
    const natural = el.getBoundingClientRect();
    el.style.position = inline;
    const dx = shown.left - natural.left;
    const dy = shown.top - natural.top;
    if (dx === 0 && dy === 0) return;
    el.dataset.screenshotPin = `${dx},${dy}`;
    stuck.push(el);
  });

  try {
    const page = await domToCanvas(document.body, {
      width,
      height,
      scale,
      backgroundColor: pageBackground(),
      style: { marginTop: `${-scrollY}px`, marginLeft: `${-scrollX}px`, height: `${document.body.scrollHeight}px` },
      filter: (node) => !(node instanceof HTMLElement && node.dataset.screenshotIgnore !== undefined),
      onCloneEachNode: (cloned) => {
        const el = cloned as HTMLElement;
        const pin = el.getAttribute?.("data-screenshot-pin");
        if (!pin) return;
        const [dx, dy] = pin.split(",").map(Number);
        el.style.setProperty("position", "relative", "important");
        el.style.setProperty("left", `${dx}px`, "important");
        el.style.setProperty("top", `${dy}px`, "important");
        el.style.setProperty("z-index", "30", "important");
      },
    });

    const out = document.createElement("canvas");
    out.width = Math.round(rect.w * scale);
    out.height = Math.round(rect.h * scale);
    const ctx = out.getContext("2d");
    if (!ctx) throw new Error("This browser can't draw a screenshot.");
    ctx.drawImage(page, Math.round(rect.x * scale), Math.round(rect.y * scale), out.width, out.height, 0, 0, out.width, out.height);

    return await new Promise<Blob>((resolve, reject) => {
      out.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("The screenshot came out empty."))), "image/png");
    });
  } finally {
    stuck.forEach((el) => delete el.dataset.screenshotPin);
  }
}

export function ScreenshotPicker({ onCapture, onCancel }: { onCapture: (blob: Blob) => void; onCancel: () => void }) {
  const [start, setStart] = useState<{ x: number; y: number } | null>(null);
  const [rect, setRect] = useState<Rect | null>(null);
  const [busy, setBusy] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopPropagation();
      onCancel();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onCancel]);

  const finish = async (box: Rect) => {
    if (box.w < MIN_SIZE || box.h < MIN_SIZE) {
      setRect(null);
      setStart(null);
      return;
    }
    setBusy(true);
    setHidden(true);
    // Two frames so the overlay is really gone before the page is drawn.
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    try {
      onCapture(await captureArea(box));
    } catch (err) {
      setHidden(false);
      setBusy(false);
      setRect(null);
      setStart(null);
      setError((err as Error)?.message || "The screenshot didn't work. Try again.");
    }
  };

  const shade = rect && rect.w > 0 && rect.h > 0;

  return createPortal(
    <div
      data-screenshot-ignore=""
      role="dialog"
      aria-modal="true"
      aria-label="Select an area for the screenshot"
      className="sq-pick"
      style={{ visibility: hidden ? "hidden" : "visible" }}
      onContextMenu={(e) => {
        e.preventDefault();
        onCancel();
      }}
      onPointerDown={(e) => {
        if (busy || e.button !== 0) return;
        (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
        setError(null);
        const point = { x: e.clientX, y: e.clientY };
        setStart(point);
        setRect({ ...point, w: 0, h: 0 });
      }}
      onPointerMove={(e) => {
        if (!start || busy) return;
        setRect(normalise(start, { x: e.clientX, y: e.clientY }));
      }}
      onPointerUp={(e) => {
        if (!start || busy) return;
        void finish(normalise(start, { x: e.clientX, y: e.clientY }));
      }}
    >
      {shade ? (
        <>
          <div className="sq-pick__dim" style={{ left: 0, right: 0, top: 0, height: rect.y }} />
          <div className="sq-pick__dim" style={{ left: 0, right: 0, top: rect.y + rect.h, bottom: 0 }} />
          <div className="sq-pick__dim" style={{ top: rect.y, height: rect.h, left: 0, width: rect.x }} />
          <div className="sq-pick__dim" style={{ top: rect.y, height: rect.h, left: rect.x + rect.w, right: 0 }} />
          <div className="sq-pick__box" style={{ left: rect.x, top: rect.y, width: rect.w, height: rect.h }}>
            <span className="sq-pick__size">
              {Math.round(rect.w)} x {Math.round(rect.h)}
            </span>
          </div>
        </>
      ) : (
        <div className="sq-pick__dim" style={{ inset: 0 }} />
      )}

      <div className="sq-pick__hint">
        {busy ? (
          <>
            <Loader2 size={15} strokeWidth={2} className="sq-spin" aria-hidden />
            Taking the screenshot
          </>
        ) : error ? (
          <span className="sq-pick__error">{error}</span>
        ) : (
          <>
            Drag a box around what you want us to see.
            <button
              type="button"
              className="sq-pick__cancel"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={onCancel}
            >
              Cancel
            </button>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
